import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { SelectionItem } from '../../../core/component/select/select.component';
import { standardCommand } from '../../../core/model/command';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormCommandHandlerResolver } from '../../../core/service/command/form-command-handler-resolver.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { RentalObjectCategory, RentalObjectCategoryToString, TenantCategory, TenantCategoryToString } from '../../model/const/enums';
import { ContactIdKind } from '../../model/contact';
import { RentApplication, RentApplicationTarget } from '../../model/rent-application';
import { RentApplicationContact } from '../../model/rent-application-contact';
import { ContactService } from '../../service/data/contact.service';
import { RentApplicationService } from '../../service/data/rent-application.service';
import { CompanyPostTarget } from '../../model/company';
import { PropertyPostTarget } from '../../model/property';
import { BuildingPostTarget } from '../../model/building';
import { DwellingPostTarget } from '../../model/dwelling';
import { PremisesPostTarget } from '../../model/premises';
import { ParkingPostTarget } from '../../model/parking';
import { OtherRentalObjectPostTarget } from '../../model/other-rental-object';
import { EntrancePostTarget } from '../../model/entrance';
import { CompanyService } from '../../service/data/company.service';
import { PropertyService } from '../../service/data/property.service';
import { BuildingService } from '../../service/data/building.service';
import { DwellingService } from '../../service/data/dwelling.service';
import { PremisesService } from '../../service/data/premises.service';
import { ParkingService } from '../../service/data/parking.service';
import { OtherRentalObjectService } from '../../service/data/other-rental-object.service';
import { EntranceService } from '../../service/data/entrance.service';
import { Observable } from 'rxjs';

@Component({
    selector: 'app-rent-application-form',
    templateUrl: './rent-application-form.component.html',
    styleUrls: ['./rent-application-form.component.scss'],
    providers: [DeleteFormCommandHandler, FormCommandHandlerResolver, { provide: DataService, useExisting: RentApplicationService }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RentApplicationFormComponent extends FormComponentBase<RentApplication> {
  constructor(
    private _fb: FormBuilder,
    private _cf: FormControlExFactory,
    private _contactService: ContactService,
    private _companyService: CompanyService,
    private _propertyService: PropertyService,
    private _buildingService: BuildingService,
    private _dwellingService: DwellingService,
    private _premisesService: PremisesService,
    private _parkingService: ParkingService,
    private _otherRentalObjectService: OtherRentalObjectService,
    private _entranceService: EntranceService,
    activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    handlerResolver: FormCommandHandlerResolver,
    service: RentApplicationService,
    private _messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, service, _messageService, navigation, commandDispatcher, handlerResolver);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
  }

  contacts: RentApplicationContact[];
  targetFilterFormGroup: FormGroup;
  companies: CompanyPostTarget[] = [];
  properties: PropertyPostTarget[] = [];
  buildings: BuildingPostTarget[] = [];
  dwellings: DwellingPostTarget[] = [];
  premises: PremisesPostTarget[] = [];
  parkings: ParkingPostTarget[] = [];
  otherRentalObjects: OtherRentalObjectPostTarget[] = [];
  entrances: EntrancePostTarget[] = [];
  private companySelectionVersion = 0;
  private propertySelectionVersion = 0;
  private buildingSelectionVersion = 0;

  protected createModel(): RentApplication {
    return new RentApplication();
  }

  public rentalObjectCategories = [
    { name: RentalObjectCategoryToString(RentalObjectCategory.Dwelling), value: RentalObjectCategory.Dwelling },
    { name: RentalObjectCategoryToString(RentalObjectCategory.Parking), value: RentalObjectCategory.Parking },
    { name: RentalObjectCategoryToString(RentalObjectCategory.Premises), value: RentalObjectCategory.Premises },
    { name: RentalObjectCategoryToString(RentalObjectCategory.Other), value: RentalObjectCategory.Other },
  ];

  protected getFormGroup(): FormGroup {
    this.targetFilterFormGroup = this._fb.group({
      company: this._cf.create('RENTAL_OBJECT_FORM.COMPANY_LABEL', ''),
      property: this._cf.create('RENTAL_OBJECT_FORM.PROPERTY_LABEL', ''),
      building: this._cf.create('DWELLING_FORM.BUILDING_LABEL', ''),
      dwelling: this._cf.create('ARTICLE.RENTAL_OBJECT_CATEGORY_DWELLING', ''),
      premises: this._cf.create('ARTICLE.RENTAL_OBJECT_CATEGORY_PREMISES', ''),
      parking: this._cf.create('ARTICLE.RENTAL_OBJECT_CATEGORY_PARKING', ''),
      otherRentalObject: this._cf.create('ARTICLE.RENTAL_OBJECT_CATEGORY_OTHER', ''),
      entrance: this._cf.create('ENTRANCE.ENTRANCE', ''),
    });
    return this._fb.group({
      rentalObjectCategory: this._cf.create('RENT_APPLICATION.OBJECT_CATEGORY', this.dataContext.rentalObjectCategory),
      contactCategory: this._cf.create('TENANT.CATEGORY', this.dataContext.contactCategory),
      contactCustomerNumber: this._cf.createReadonly('TENANT.CONTACT_NUMBER', this.dataContext.contactCustomerNumber),
      contactName: this._cf.createWithOptions('TENANT.NAME', this.dataContext.contactName, { length: 100, required: true }),
      applicationDate: this._fb.group({}),
      contactEmail: this._cf.createWithOptions('TENANT.EMAIL', this.dataContext.contactEmail, { length: 255, required: false }),
      contactPhone: this._cf.createWithOptions('TENANT.PHONE', this.dataContext.contactPhone, { length: 20, required: false }),
      idType: this._cf.create('TENANT.ID_TYPE.ID_TYPE', this.dataContext.idType),
      contactIdentification: this._cf.create('TENANT.ID_TYPE.ID_NUMBER', this.dataContext.contactIdentification),
      numberOfRoomsMin: this._cf.create('RENT_APPLICATION.MIN_ROOMS', this.dataContext.numberOfRoomsMin),
      numberOfRoomsMax: this._cf.create('RENT_APPLICATION.MAX_ROOMS', this.dataContext.numberOfRoomsMax),
      maxYearRent: this._cf.create('RENT_APPLICATION.MAX_YEAR_RENT', this.dataContext.maxYearRent),
      maxMonthRent: this._cf.create(
        'RENT_APPLICATION.MAX_MONTH_RENT',
        this.dataContext.maxYearRent == null ? null : this.dataContext.maxYearRent / 12
      ),
      comment: this._cf.createWithOptions('COMMON.NOTE', this.dataContext.comment, { length: 1000, required: false }),
      contactId: this._cf.createHidden(this.dataContext.contactId),
      contactIdExistingContact: this._cf.create('TENANT.EDIT_HEADER', this.dataContext.contactIdExistingContact),
      targets: this._fb.array((this.dataContext.targets ?? []).map(target => this.targetGroup(target))),
    });
  }

  private _companyIdTypes: SelectionItem[] = [];
  private _personIdTypes: SelectionItem[] = [];
  get idTypes(): SelectionItem[] {
    if (!this.form) {
      return [];
    }
    return this.form.controls['contactCategory'].value === TenantCategory.Company ? this._companyIdTypes : this._personIdTypes;
  }

  get idType(): ContactIdKind {
    return this.form ? this.form.controls['idType'].value : null;
  }

  protected init(data: RentApplication) {
    super.init(data);
    this.form.controls['contactCategory'].updateValueAndValidity();
    this.form.controls['maxYearRent'].valueChanges.subscribe((value: number) => {
      let monthRent = value == null ? null : value / 12;
      if (this.form.controls['maxMonthRent'].value == monthRent) return;
      this.form.controls['maxMonthRent'].setValue(monthRent);
    });

    this.AddSubscription(
      this._contactService.getIdTypes().subscribe(types => {
        this._companyIdTypes = types.filter(t => t.category === TenantCategory.Company).map(t => new SelectionItem(t.caption, t.idType));
        this._personIdTypes = types
          .filter(t => t.category === TenantCategory.PhysicalPerson)
          .map(t => new SelectionItem(t.caption, t.idType));
        if (this.isNew) {
          this.form.controls['idType'].setValue(this.idTypes[0].value);
        }
      })
    );

    this.form.controls['maxMonthRent'].valueChanges.subscribe((value: number) => {
      let yearRent = value == null ? null : value * 12;
      if (this.form.controls['maxYearRent'].value == yearRent) return;
      this.form.controls['maxYearRent'].setValue(yearRent);
    });

    this.form.controls['contactIdExistingContact'].valueChanges.subscribe(value => {
      if (value instanceof RentApplicationContact) {
        this.form.controls['contactName'].setValue(value.contactName);
        this.form.controls['contactCategory'].setValue(value.contactCategory);
        this.form.controls['contactEmail'].setValue(value.contactEmail);
        this.form.controls['contactPhone'].setValue(value.contactPhone);
        this.form.controls['contactIdentification'].setValue(value.contactIdentification);
        this.form.controls['idType'].setValue(value.idType);
      } else {
        this.form.controls['contactName'].setValue(null);
        this.form.controls['contactEmail'].setValue(null);
        this.form.controls['contactPhone'].setValue(null);
        this.form.controls['contactIdentification'].setValue(null);
        this.form.controls['idType'].setValue(ContactIdKind.None);
        this.form.controls['contactCategory'].setValue(TenantCategory.PhysicalPerson);
      }
    });

    this._contactService.getRentApplicationContacts().subscribe(items => {
      this.contacts = items;
      const contact = this.contacts.find(x => x.id === this.dataContext.contactIdExistingContact);
      if (contact) {
        this.form.controls['contactIdExistingContact'].setValue(contact);
      } else if (this.dataContext.contactIdExistingContact) {
        this._contactService.getRentApplicationContact(this.dataContext.contactIdExistingContact).subscribe(item => {
          this.contacts.push(item);
          this.form.controls['contactIdExistingContact'].setValue(item);
        });
      }
    });

    this.AddSubscription(this._companyService.getPostTargetModels().subscribe({
      next: items => (this.companies = items),
      error: error => {
        this.companies = [];
        this._messageService.showError(error);
      },
    }));
    this.AddSubscription(this.targetFilterFormGroup.controls['company'].valueChanges.subscribe((company: CompanyPostTarget) => {
      const requestVersion = ++this.companySelectionVersion;
      ++this.propertySelectionVersion;
      ++this.buildingSelectionVersion;
      this.resetTargetControls(['property', 'building', 'dwelling', 'premises', 'parking', 'otherRentalObject', 'entrance']);
      if (company?.id) this.loadTargetOptions(requestVersion, () => this.companySelectionVersion, this._propertyService.getPostTargetModels(company.id), items => (this.properties = items));
    }));
    this.AddSubscription(this.targetFilterFormGroup.controls['property'].valueChanges.subscribe((property: PropertyPostTarget) => {
      const requestVersion = ++this.propertySelectionVersion;
      ++this.buildingSelectionVersion;
      this.resetTargetControls(['building', 'dwelling', 'premises', 'parking', 'otherRentalObject', 'entrance']);
      if (!property?.id) return;
      this.loadTargetOptions(requestVersion, () => this.propertySelectionVersion, this._buildingService.getPostTargetModels(property.id), items => (this.buildings = items));
      this.loadTargetOptions(requestVersion, () => this.propertySelectionVersion, this._parkingService.getPostTargetModels(property.id), items => (this.parkings = items));
      this.loadTargetOptions(requestVersion, () => this.propertySelectionVersion, this._otherRentalObjectService.getPostTargetModels(property.id), items => (this.otherRentalObjects = items));
    }));
    this.AddSubscription(this.targetFilterFormGroup.controls['building'].valueChanges.subscribe((building: BuildingPostTarget) => {
      const requestVersion = ++this.buildingSelectionVersion;
      this.resetTargetControls(['dwelling', 'premises', 'entrance']);
      if (!building?.id) return;
      this.loadTargetOptions(requestVersion, () => this.buildingSelectionVersion, this._dwellingService.getPostTargetModels(building.id), items => (this.dwellings = items));
      this.loadTargetOptions(requestVersion, () => this.buildingSelectionVersion, this._premisesService.getPostTargetModels(building.id), items => (this.premises = items));
      this.loadTargetOptions(requestVersion, () => this.buildingSelectionVersion, this._entranceService.getPostTargetModels(building.id), items => (this.entrances = items));
    }));
  }

  public get dwellingSelected(): boolean {
    return this.form.controls['rentalObjectCategory'].value == RentalObjectCategory.Dwelling;
  }

  contactCategories = [
    { name: TenantCategoryToString(TenantCategory.PhysicalPerson), value: TenantCategory.PhysicalPerson },
    { name: TenantCategoryToString(TenantCategory.Company), value: TenantCategory.Company },
  ];

  applyTarget(): void {
    const selected = this.targetFilterFormGroup.value;
    const field = ['dwelling', 'premises', 'parking', 'otherRentalObject', 'entrance', 'building', 'property', 'company'].find(key => selected[key]?.id);
    if (!field) return;
    const target = new RentApplicationTarget();
    (target as any)[`${field}Id`] = selected[field].id;
    target.description = selected[field].toString();
    (this.form.controls['targets'] as FormArray).push(this.targetGroup(target));
    this.resetAppliedTargetSelection(field);
    this.form.markAsDirty();
  }

  deleteTarget(index: number): void {
    (this.form.controls['targets'] as FormArray).removeAt(index);
    this.form.markAsDirty();
  }

  targetLabel(target: RentApplicationTarget): string {
    if (target.description) return target.description;
    const field = Object.keys(target).find(key => key.endsWith('Id') && key !== 'id' && target[key]);
    return field ? `${field.replace('Id', '')}: ${(target as any)[field]}` : '';
  }

  private targetGroup(target: RentApplicationTarget): FormGroup {
    return this._fb.group({
      id: [target.id], companyId: [target.companyId], propertyId: [target.propertyId], buildingId: [target.buildingId],
      dwellingId: [target.dwellingId], premisesId: [target.premisesId], parkingId: [target.parkingId],
      otherRentalObjectId: [target.otherRentalObjectId], entranceId: [target.entranceId], childScope: [target.childScope], description: [target.description],
    });
  }

  private resetTargetControls(names: string[]): void {
    names.forEach(name => {
      this.targetFilterFormGroup.controls[name].setValue('', { emitEvent: false });
      this.clearTargetOptions(name);
    });
  }

  private resetAppliedTargetSelection(field: string): void {
    const leafControls = ['dwelling', 'premises', 'parking', 'otherRentalObject', 'entrance'];
    const controls = field === 'company'
      ? ['company', 'property', 'building', ...leafControls]
      : field === 'property'
        ? ['property', 'building', ...leafControls]
        : field === 'building'
          ? ['building', ...leafControls]
          : leafControls;

    controls.forEach(name => this.targetFilterFormGroup.controls[name].setValue('', { emitEvent: false }));
  }

  private clearTargetOptions(name: string): void {
    switch (name) {
      case 'property': this.properties = []; break;
      case 'building': this.buildings = []; break;
      case 'dwelling': this.dwellings = []; break;
      case 'premises': this.premises = []; break;
      case 'parking': this.parkings = []; break;
      case 'otherRentalObject': this.otherRentalObjects = []; break;
      case 'entrance': this.entrances = []; break;
    }
  }

  private loadTargetOptions<T>(requestVersion: number, currentVersion: () => number, request: Observable<T[]>, setOptions: (items: T[]) => void): void {
    this.AddSubscription(request.subscribe({
      next: items => {
        if (requestVersion === currentVersion()) setOptions(items);
      },
      error: error => {
        if (requestVersion !== currentVersion()) return;
        setOptions([]);
        this._messageService.showError(error);
      },
    }));
  }
}
